import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Users, Shield, User, Ban, CheckCircle2, Search, Key, Trash2, Pencil, X } from "lucide-react";

const ROLE_STYLES = {
  admin: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200", icon: Shield },
  agent: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", icon: Users },
  customer: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", icon: User },
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editing, setEditing] = useState(null); // user object being edited

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data.users);
      setCounts(data.counts);
    } catch (err) {
      toast.error("Could not load users", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? `Disabled ${u.name}` : `Enabled ${u.name}`);
      load();
    } catch (err) {
      toast.error("Update failed", { description: formatApiError(err) });
    }
  };

  const resetPassword = async (u) => {
    const pwd = window.prompt(`Set a new password for ${u.name} (${u.email}).\nMinimum 6 characters. They can change it later.`);
    if (pwd == null) return;
    if (pwd.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.patch(`/users/${u.id}`, { password: pwd });
      toast.success(`Password updated for ${u.name}`);
    } catch (err) {
      toast.error("Update failed", { description: formatApiError(err) });
    }
  };

  const deleteUser = async (u) => {
    const label = u.role === "admin" ? "admin" : u.role === "agent" ? "agent" : "customer";
    if (!window.confirm(`Delete ${label} ${u.name} (${u.email})? This is permanent.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name} deleted`);
      load();
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    }
  };

  const filtered = users.filter((u) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (q) {
      const s = q.toLowerCase();
      if (![u.name, u.email].some((x) => (x || "").toLowerCase().includes(s))) return false;
    }
    return true;
  });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-display tracking-tight">All users</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {me.role === "admin" ? "Enable or disable anyone on the platform." : "Directory view — full user list (admins can enable/disable)."}
          </p>
        </div>
        <div className="flex gap-2">
          {["admin", "agent", "customer"].map((r) => (
            <div key={r} className={`rounded-xl border px-3 py-2 ${ROLE_STYLES[r].bg} ${ROLE_STYLES[r].border}`}>
              <div className={`text-[10px] font-mono uppercase tracking-widest ${ROLE_STYLES[r].text}`}>{r}s</div>
              <div className="text-lg font-bold text-slate-900 font-display leading-tight">{counts[r] ?? 0}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="beacon-card p-3 flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="beacon-input pl-9" placeholder="Search name or email..."
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search-input" />
        </div>
        <select className="beacon-input w-auto" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="users-role-filter">
          <option value="all">All roles</option>
          <option value="customer">Customer</option>
          <option value="agent">Agent</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      <div className="beacon-card divide-y divide-slate-200">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading users...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No users match these filters.</div>
        ) : (
          filtered.map((u) => {
            const style = ROLE_STYLES[u.role] || ROLE_STYLES.customer;
            const Icon = style.icon;
            const isMe = u.id === me.id;
            const isSubOnly = u.source === "subscription";
            return (
              <div key={u.id} className="p-4 sm:p-5 flex items-center justify-between gap-3 flex-wrap" data-testid={`user-row-${u.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-xl grid place-items-center ${style.bg} ${style.text}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">
                      {u.name} {isMe && <span className="text-[10px] font-mono ml-1 bg-slate-100 rounded px-1.5 py-0.5">you</span>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {u.email}{u.phone && <span> · <span className="font-mono">{u.phone}</span></span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`beacon-badge ${style.bg} ${style.text} ${style.border}`}>{u.role}</span>
                  {u.subscription_count > 0 && (
                    <span className="beacon-badge bg-indigo-50 text-indigo-700 border-indigo-200" data-testid={`user-sub-count-${u.id}`}>
                      {u.subscription_count} subscription{u.subscription_count === 1 ? "" : "s"}
                    </span>
                  )}
                  {!isSubOnly && (
                    <span className={`beacon-badge ${
                      u.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"
                    }`}>
                      <span className={`beacon-dot ${u.is_active ? "bg-emerald-500" : "bg-slate-400"}`} />
                      {u.is_active ? "active" : "disabled"}
                    </span>
                  )}
                  {!isMe && me.role === "admin" && (
                    <>
                      <Button size="sm" variant="outline"
                        onClick={() => setEditing(u)}
                        data-testid={`user-edit-${u.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                      {!isSubOnly && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => resetPassword(u)} data-testid={`user-reset-password-${u.id}`}>
                            <Key className="w-3.5 h-3.5 mr-1" /> Password
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => toggleActive(u)} data-testid={`user-toggle-${u.id}`}>
                            {u.is_active ? <><Ban className="w-3.5 h-3.5 mr-1" /> Disable</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Enable</>}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteUser(u)}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            data-testid={`user-delete-${u.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <CustomerEditModal
        user={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

function CustomerEditModal({ user, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setPhone(user.phone || "");
    }
  }, [user]);

  if (!user) return null;

  const hasSubs = (user.subscription_count || 0) > 0;

  const save = async () => {
    const nm = name.trim();
    const em = email.trim().toLowerCase();
    const ph = phone.trim();
    if (!nm) { toast.error("Company / name cannot be empty"); return; }
    if (em && !/^[^@]+@[^@]+\.[^@]+$/.test(em)) { toast.error("Invalid email"); return; }
    setSaving(true);
    try {
      if (hasSubs) {
        // Customer has subscriptions — propagate to every service under this company
        // (and the linked login account, if any) via the company-scoped endpoint.
        const { data } = await api.patch("/users/company", {
          current_customer_name: user.name,
          customer_name: nm,
          customer_email: em || null,
          customer_phone: ph || null,
        });
        toast.success(
          `Updated ${data.matched} subscription${data.matched === 1 ? "" : "s"}` +
          (data.linked_user_updated ? " and linked login" : "")
        );
      } else {
        // Standalone user (no subscriptions) — edit the user record directly.
        await api.patch(`/users/${user.id}`, {
          name: nm,
          email: em || null,
          phone: ph || null,
        });
        toast.success("User updated");
      }
      onSaved();
    } catch (err) {
      toast.error("Update failed", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-md overflow-hidden"
        data-testid="user-edit-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900">Edit {hasSubs ? "customer" : (user.role || "user")}</h3>
            <p className="text-xs text-slate-500 mt-1">
              {hasSubs
                ? `Changes propagate to every service (${user.subscription_count}) under this company${user.source !== "subscription" ? " and the linked login account" : ""}.`
                : "Update this account's name, email or phone."}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1"
            data-testid="user-edit-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="beacon-label">{hasSubs ? "Company / customer name" : "Full name"}</label>
            <input className="beacon-input" value={name} onChange={(e) => setName(e.target.value)}
              data-testid="user-edit-name-input" />
          </div>
          <div>
            <label className="beacon-label">Email</label>
            <input className="beacon-input font-mono" type="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="user-edit-email-input" />
          </div>
          <div>
            <label className="beacon-label">Phone</label>
            <input className="beacon-input font-mono" value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="user-edit-phone-input" />
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}
            data-testid="user-edit-save-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white">
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
